import { useEffect } from 'react';
import { useLocation } from '@react-router';
import type { GetContactCollectionByMagicLinkQuery } from '../../../../admin/contactCollector/api/ContactCollector.graphql.generated';
import { useGetDataForContactCollectionGuestQuery } from '../../../../admin/contactCollector/api/ContactCollector.graphql.generated';
import { Category } from '@graphql/generated-base';
import { useLoadGoogleFont } from '@shared/hooks/useLoadGoogleFont';
import { useRouterHelper, useTranslation } from '@shared/core';
import { useContactCollectorGuestTelemetry } from '../../ContactCollectorGuest.telemetry';
import { withWindow } from '@shared/utils/withWindow';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { contactCollectorGuestRoutePaths } from '../../contactCollectorGuest.routes';
import { CONTACT_COLLECTOR_UTM } from '../../contactCollector.constants';
import type { Context } from '../../shared/types';

const removeTimeFromDate = (date: string) => {
  if (!date) {
    return date;
  }
  const array = date.split(' ');
  if (date.length < 4) {
    return date;
  }
  return [array[0], array[1], array[2], array[3]].join(' ');
};

export const useLandingController = (setContext: React.Dispatch<React.SetStateAction<Context>>, data?: GetContactCollectionByMagicLinkQuery) => {
  const routeHelpers = useRouterHelper();
  const location = useLocation();
  const isMobile = useIsMobileScreen();
  const { trackError, pageViewed, buttonInteracted } = useContactCollectorGuestTelemetry();
  const {
    data: queryData,
    loading,
    error,
  } = useGetDataForContactCollectionGuestQuery({
    batchMode: 'fast',
    ssr: false,
    variables: {
      eventId: data?.getContactCollectionByMagicLink?.eventId ?? '',
    },
    skip: !data?.getContactCollectionByMagicLink?.eventId,
    onError: error => trackError('Query', error),
  });

  const eventTitleFont = queryData?.eventById?.eventDesign?.fonts?.find(font => font.category === Category.EVENT_TITLE)?.font?.fontFamily;
  const fontFamily = eventTitleFont ?? queryData?.eventById?.eventDesign?.font?.fontFamily ?? 'PT Serif';
  const fontTextTransform = eventTitleFont ? 'none' : (queryData?.eventById?.eventDesign?.font?.textTransform ?? 'none');
  useLoadGoogleFont([`${fontFamily}:400`]);
  const { t } = useTranslation('contactCollector');
  const tAdmin = t('admin');
  const info = queryData?.eventById?.info;
  const eventDisplayName = info?.eventDisplayName || tAdmin.theCouple();
  const date = removeTimeFromDate(info?.dateV0918?.dateString ?? '');
  const coupleNames = (info?.ownerFirstName && info?.fianceeFirstName ? `${info?.ownerFirstName} & ${info?.fianceeFirstName}` : info?.ownerFirstName) || tAdmin.theCouple();
  const website = queryData?.eventById?.website ?? '';

  useEffect(() => {
    if (!loading) {
      pageViewed.enter('landingPage', { eventHandle: website });
      setContext({
        coupleNames,
        eventDisplayName,
        date,
        eventDateDisplayEnabled: !!data?.getContactCollectionByMagicLink?.eventDateDisplayEnabled,
        website,
        websiteLinkEnabled: !!data?.getContactCollectionByMagicLink?.websiteLinkEnabled,
        fontFamily,
        fontTextTransform,
        mediaType: data?.getContactCollectionByMagicLink?.mediaType ?? '',
        mediaSource: data?.getContactCollectionByMagicLink?.mediaSource ?? '',
        firebaseId: queryData?.eventById?.firebaseId ?? '',
      });
    }
  }, [
    coupleNames,
    data?.getContactCollectionByMagicLink?.eventDateDisplayEnabled,
    data?.getContactCollectionByMagicLink?.websiteLinkEnabled,
    date,
    eventDisplayName,
    pageViewed,
    loading,
    setContext,
    website,
    fontFamily,
    fontTextTransform,
    data?.getContactCollectionByMagicLink?.mediaType,
    data?.getContactCollectionByMagicLink?.mediaSource,
    queryData?.eventById?.firebaseId,
  ]);

  const handleOnVisitEventWebsiteClick = () => {
    buttonInteracted({ buttonType: 'navigation', element: 'visitEventWebsite' });
    withWindow(() => {
      window.open(`${window.location.origin}/${website}?${CONTACT_COLLECTOR_UTM}`, '_blank');
    });
  };

  const handleOnProvideDetailsClick = () => {
    buttonInteracted({ buttonType: 'navigation', element: 'provideDetails' });
    routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.form.path, location.search);
  };

  return {
    fontFamily,
    fontTextTransform,
    tAdmin,
    eventDisplayName,
    coupleNames,
    date,
    loading,
    error,
    handleOnProvideDetailsClick,
    handleOnVisitEventWebsiteClick,
    isMobile,
  };
};
